import * as React from 'react';
import { useState, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import GnssCoordinateList from './list';
import GnssCoordinateCreate from './create';
import GnssCoordinateCurrent from './current';
import GPSStatus from './GPSStatus';
import { LinearProgress } from '@mui/material';
import ReceiverService from '../receiverService';
import CoordinateMapView from './CoordinateMapView';
import Button from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const service = new ReceiverService;

export default function GnssCoordinate() {
  const [loading, setLoading] = useState(true);


  const [largescreen, setLargescreen] = React.useState({
    matches: window.innerWidth > 768 ? true : false,
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const handler = (e) => setLargescreen({ matches: e.matches });

    // Use addEventListener instead of deprecated addListener
    mediaQuery.addEventListener('change', handler);

    // Cleanup function to remove the listener
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const [coordinates, setCoordinates] = useState([]);
  const [center, setCenter] = useState([]);
  const [highlight, setHighlight] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  function fetchData() {
    console.log('coordinate.js: fetchData called');
    setLoading(true);
    service.getCoordinates().then(data => {
      console.log('coordinate.js: Got coordinates data, length:', data.length, 'current center:', center);

      // Only update coordinates if they actually changed
      setCoordinates(prevCoords => {
        // If lengths differ, update
        if (prevCoords.length !== data.length) {
          console.log('coordinate.js: Coordinates length changed, updating');
          return data;
        }

        // Check if any coordinate values changed
        const hasChanged = data.some((coord, index) => {
          const prevCoord = prevCoords[index];
          return !prevCoord ||
                 coord.latitude !== prevCoord.latitude ||
                 coord.longitude !== prevCoord.longitude ||
                 coord.altitude !== prevCoord.altitude;
        });

        if (hasChanged) {
          console.log('coordinate.js: Coordinate values changed, updating');
          return data;
        }

        console.log('coordinate.js: Coordinates unchanged, keeping previous array reference');
        return prevCoords;
      });

      // Only set center on initial load when center is empty
      if (data.length > 0 && center.length === 0) {
        console.log('coordinate.js: Setting initial center to', data[0].latitude, data[0].longitude);
        setCenter([data[0].latitude, data[0].longitude]);
      }
      setLoading(false);
    });
  }

  const SetApproxButton = () => {
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [result, setResult] = useState(false);
    const [message, setMessage] = useState("");
    const [progress, setProgress] = useState(0);
    const [statusMessage, setStatusMessage] = useState("");
    const [ws, setWs] = useState(null);

    const submit = () => {
      setLoading(true);
      setProgress(0);
      setStatusMessage("Starting...");

      // Start the async operation and get task_id
      service.setApproxCoordinateAsync()
        .then((response) => {
          const taskId = response.task_id;

          // Connect to WebSocket for progress updates
          const session = JSON.parse(localStorage.getItem("session"));
          const token = session?.access_token;

          // Construct WebSocket URL
          const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
          const wsHost = window.location.host;
          const wsUrl = `${wsProtocol}//${wsHost}/ws/gnss/approxpos/${taskId}/?token=${token}`;

          const websocket = new WebSocket(wsUrl);

          websocket.onopen = () => {
            console.log('WebSocket connected for approx position');
          };

          websocket.onmessage = (event) => {
            const data = JSON.parse(event.data);

            if (data.type === 'progress') {
              // Update progress bar and status message
              setProgress(data.progress);
              setStatusMessage(data.message);
            } else if (data.type === 'result') {
              // Final result
              setProgress(100);
              setLoading(false);
              setSubmitted(true);

              if (data.status === 'success') {
                setResult(true);
                setStatusMessage(data.message);
                fetchData(); // Refresh the coordinates list
              } else {
                setResult(false);
                setMessage(data.error || 'Failed to acquire position');
              }

              // Close WebSocket
              websocket.close();
            }
          };

          websocket.onerror = (error) => {
            console.error('WebSocket error:', error);
            setLoading(false);
            setSubmitted(true);
            setResult(false);
            setMessage("WebSocket connection error");
            websocket.close();
          };

          websocket.onclose = () => {
            console.log('WebSocket closed');
          };

          setWs(websocket);
        })
        .catch((error) => {
          setLoading(false);
          setSubmitted(true);
          setResult(false);
          setMessage("Error starting position acquisition");
        });
    };

    return (
      <React.Fragment>
        <Button variant="contained" onClick={submit} disabled={(loading || submitted)}>
          {loading ? <CircularProgress size="1rem" color="inherit" /> : <React.Fragment>Set to Current(Approx.)</React.Fragment>}
        </Button>
        {loading && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {statusMessage}
            </Typography>
          </Box>
        )}
        {!loading && submitted && result && (
          <Alert severity="success" sx={{ mt: 2 }}>{statusMessage}</Alert>
        )}
        {!loading && submitted && !result && (
          <Alert severity="error" sx={{ mt: 2 }}>{message}</Alert>
        )}
      </React.Fragment>
    );
  }


  function zoomTo(lat, lon) {
    setHighlight([lat, lon]);
    setCenter([lat, lon]);
  }

  function highlightCoord(lat, lon) {
    setHighlight([lat, lon]);
  }

  if (loading) {
    return <LinearProgress />;
  }
  else if (coordinates.length === 0) {
    return (
      <React.Fragment>
        <h1 className="page-title"><span className="fw-semi-bold">GNSS Reference Coordinate</span></h1>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Stack spacing={2} direction="column">
              <SetApproxButton />
              <GnssCoordinateCreate
                refresh={() => fetchData()}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <GPSStatus />
          </Grid>
        </Grid>
      </React.Fragment>
    );
  }
  else {
    return (
      <React.Fragment>
        <h1 className="page-title"><span className="fw-semi-bold">GNSS Reference Coordinate</span></h1>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Stack spacing={2} direction="column">
              <CoordinateMapView coordinates={coordinates} center={center} highlight={highlight} zoom={18} />
              <Button
                variant="contained"
                onClick={() => setCenter([coordinates[0].latitude, coordinates[0].longitude])}
              >
                Zoom to Current
              </Button>
              <Button
                variant="contained"
                onClick={() => fetchData()}
              >
                Refresh
              </Button>
              <GnssCoordinateCurrent coordinate={coordinates[0]} />
              <SetApproxButton />
              {largescreen.matches && <GnssCoordinateList coords={coordinates} zoomTo={zoomTo} highlightCoord={highlightCoord} refresh={() => fetchData()} />}
              <GnssCoordinateCreate
                currentcoord={{
                  latitude: coordinates[0].latitude,
                  longitude: coordinates[0].longitude,
                  altitude: coordinates[0].altitude
                }}
                refresh={() => fetchData()} />
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <GPSStatus />
          </Grid>
        </Grid>
      </React.Fragment>
    );
  }
}
